<script setup lang="ts">
import {
  useBillingStore,
  type BillingTermKind,
  type CatalogOffering,
  type CatalogTerm,
} from '~/store/billing'

/**
 * Billing-cycle chooser.
 *
 * The old control was a row of buttons carrying a bare `-32%` badge: a
 * percentage with no visible anchor, computed off an arbitrary plan, next to a
 * plan table whose "total do ciclo" column silently changed meaning (a month of
 * spend vs a year of it) every time the cycle changed. Committing therefore
 * *looked* more expensive.
 *
 * Each cycle is now a priced card quoting the SAME unit — one month — for the
 * plan actually being bought, with the money saved spelled out and on-demand
 * shown as the explicit baseline the percentages are measured against.
 */
const props = defineProps<{
  modelValue: BillingTermKind
  terms: CatalogTerm[]
  /** Plan being priced. Null before one is picked — then we quote the cheapest. */
  offering: CatalogOffering | null
  /** Every COMPUTE plan, for the "from R$ x" anchor while none is selected. */
  offerings: CatalogOffering[]
  /** Existing-subscription checkout: the cycle is inherited, not chosen. */
  locked?: boolean
}>()

const emit = defineEmits<{ 'update:modelValue': [BillingTermKind] }>()

const billing = useBillingStore()
const labels = useBillingLabels()
const pricing = useCheckoutPricing()

/** Committed cycles first (cheapest per month), on-demand last as the baseline. */
const orderedTerms = computed(() => {
  const committed = props.terms.filter((t) => t.kind !== 'ON_DEMAND')
  const onDemand = props.terms.filter((t) => t.kind === 'ON_DEMAND')
  return [
    ...committed.sort(
      (a, b) => (a.commitment_hours ?? 0) - (b.commitment_hours ?? 0)
    ),
    ...onDemand,
  ]
})
const displayedTerms = computed(() =>
  props.locked
    ? orderedTerms.value.filter((term) => term.kind === props.modelValue)
    : orderedTerms.value
)

/**
 * The plan the cards quote. Falls back to the cheapest plan so the picker is
 * meaningful on arrival — the customer sees what a cycle costs before having to
 * commit to a size.
 */
const quotedOffering = computed(
  () =>
    props.offering ??
    pricing.cheapestOffering(props.offerings, 'ON_DEMAND') ??
    props.offerings[0] ??
    null
)
const isFromPrice = computed(() => !props.offering)

function rateFor(kind: BillingTermKind): string | null {
  return quotedOffering.value?.rates_by_term[kind] ?? null
}

function monthlyPrice(kind: BillingTermKind): string {
  return pricing.fmt(pricing.perMonth(rateFor(kind)))
}

function cyclePrice(kind: BillingTermKind): string {
  return pricing.fmt(pricing.perCycle(rateFor(kind), kind))
}

function hourlyPrice(kind: BillingTermKind): string {
  const rate = rateFor(kind)
  return rate == null ? '—' : billing.formatMicroCents(rate)
}

function savings(kind: BillingTermKind) {
  return pricing.savingsVsOnDemand(quotedOffering.value?.rates_by_term, kind)
}

/** The cycle that saves the most — the one worth calling out. */
const bestValueKind = computed<BillingTermKind | null>(() => {
  let best: BillingTermKind | null = null
  let bestPct = 0
  for (const term of props.terms) {
    const pct = savings(term.kind)?.pct ?? 0
    if (pct > bestPct) {
      bestPct = pct
      best = term.kind
    }
  }
  return best
})

function select(kind: BillingTermKind) {
  if (props.locked) return
  emit('update:modelValue', kind)
}
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-start justify-between gap-3 flex-wrap">
      <p class="text-xs text-muted">
        {{
          isFromPrice
            ? $t('billing.page.checkout.term.hintFrom')
            : $t('billing.page.checkout.term.hint', {
                plan: quotedOffering?.name ?? '',
              })
        }}
      </p>
      <UBadge
        v-if="locked"
        color="neutral"
        variant="soft"
        icon="i-lucide-lock"
        size="sm"
      >
        {{ $t('billing.page.checkout.term.locked') }}
      </UBadge>
    </div>

    <div
      :class="[
        'grid gap-2.5',
        locked ? 'max-w-sm grid-cols-1' : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-5',
      ]"
    >
      <button
        v-for="term in displayedTerms"
        :key="term.kind"
        type="button"
        data-testid="billing-term-option"
        :disabled="locked && modelValue !== term.kind"
        :aria-pressed="modelValue === term.kind"
        :class="[
          'relative flex flex-col gap-1 rounded-xl border-2 p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]',
          modelValue === term.kind
            ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
            : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
          locked && modelValue !== term.kind && 'opacity-40 cursor-not-allowed',
        ]"
        @click="select(term.kind)"
      >
        <!-- Best-value flag sits above the card so it reads as an award on the
             option, not as another price line inside it. -->
        <span
          v-if="term.kind === bestValueKind && !locked"
          class="absolute -top-2 left-3 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
        >
          {{ $t('billing.page.checkout.term.bestValue') }}
        </span>

        <!-- Fixed-height header keeps every card's price on the same baseline,
             even when a longer cycle name wraps to two lines. -->
        <div class="flex items-start justify-between gap-1 min-h-9">
          <span class="text-sm font-semibold leading-tight">{{ labels.termLabel[term.kind] }}</span>
          <!-- Always `soft` with darkened text. The selected card used to swap
               to `solid`, i.e. white on the success fill, which measures
               2.37:1 — so the discount badge became unreadable at exactly the
               moment the customer chose that cycle. -->
          <UBadge
            v-if="savings(term.kind)"
            size="sm"
            color="success"
            variant="soft"
            class="shrink-0 text-success-800 dark:text-success-300"
          >
            −{{ savings(term.kind)!.pct }}%
          </UBadge>
          <UBadge
            v-else-if="term.kind === 'ON_DEMAND'"
            size="sm"
            color="neutral"
            variant="soft"
            class="shrink-0"
          >
            {{ $t('billing.page.checkout.term.baseline') }}
          </UBadge>
        </div>

        <div class="leading-tight">
          <span v-if="isFromPrice" class="block text-[11px] text-toned">
            {{ $t('billing.page.checkout.term.from') }}
          </span>
          <span class="text-lg font-bold tabular-nums">{{ monthlyPrice(term.kind) }}</span>
          <span class="text-xs text-toned">{{ $t('billing.page.checkout.term.perMonth') }}</span>
        </div>

        <!-- What actually gets charged, so a normalised monthly figure can never
             be mistaken for the amount on the invoice. -->
        <p class="text-[11px] text-toned leading-snug">
          <template v-if="term.kind === 'ON_DEMAND'">
            {{ $t('billing.page.checkout.term.onDemandNote', { rate: hourlyPrice(term.kind) }) }}
          </template>
          <!-- A one-month commitment is "billed monthly", not "every 1 months",
               and its cycle total is the price already shown above. -->
          <template v-else-if="pricing.commitmentMonths(term.kind) === 1">
            {{ $t('billing.page.checkout.term.billedMonthly') }}
          </template>
          <template v-else>
            {{
              $t('billing.page.checkout.term.billedEvery', {
                total: cyclePrice(term.kind),
                months: pricing.commitmentMonths(term.kind) ?? 1,
              })
            }}
          </template>
        </p>

        <p
          v-if="savings(term.kind)"
          class="text-[11px] font-medium text-success-800 dark:text-success-300 flex items-center gap-1"
        >
          <UIcon name="i-lucide-piggy-bank" class="size-3 shrink-0" />
          {{
            $t('billing.page.checkout.term.youSave', {
              amount: pricing.fmt(savings(term.kind)!.perMonthMicro),
            })
          }}
        </p>
      </button>
    </div>

    <p class="text-[11px] text-muted flex items-start gap-1.5">
      <UIcon name="i-lucide-info" class="size-3.5 shrink-0 mt-px" />
      {{ $t('billing.page.checkout.term.footnote') }}
    </p>
  </div>
</template>
